import { create } from "zustand";

import { ApiError } from "../../api/client";

/** Ré-authentification des gestes d'administration (docs/17 §4.6) : le serveur refuse une écriture d'administration
 *  quand le mot de passe n'a pas été saisi depuis 10 minutes (403 `REAUTH_REQUIRED`). Le client ouvre alors la
 *  fenêtre « Confirmez votre mot de passe » (`ReauthHost`), puis rejoue la requête une fois. Le mot de passe ne vit
 *  que dans l'état de la fenêtre. */

export const REAUTH_REQUIRED = "REAUTH_REQUIRED";

/** Fenêtre refermée sans confirmer : le geste n'a pas eu lieu, sans message d'erreur. */
export class ReauthCancelled extends Error {
  constructor() {
    super("reauth-cancelled");
  }
}

interface ReauthState {
  /** Réponse attendue par le geste en cours (vrai : mot de passe confirmé). */
  pending: ((confirmed: boolean) => void) | null;
  ask: () => Promise<boolean>;
  answer: (confirmed: boolean) => void;
}

export const useReauth = create<ReauthState>((set, get) => ({
  pending: null,
  ask: () =>
    new Promise<boolean>((resolve) => {
      get().pending?.(false); // une seule fenêtre : la demande précédente est abandonnée
      set({ pending: resolve });
    }),
  answer: (confirmed) => {
    const pending = get().pending;
    set({ pending: null });
    pending?.(confirmed);
  },
}));

export function reauthRequired(error: unknown): boolean {
  return error instanceof ApiError && error.code === REAUTH_REQUIRED;
}

/** Geste d'administration : rejoué une fois après la confirmation du mot de passe ; `ReauthCancelled` si la
 *  personne renonce. `ask` : injectable pour les essais. */
export async function withReauth<T>(run: () => Promise<T>, ask: () => Promise<boolean> = () => useReauth.getState().ask()): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (!reauthRequired(error)) throw error;
    if (!(await ask())) throw new ReauthCancelled();
    return run();
  }
}
